<script lang="ts">
	import ArticleCard from '$lib/ArticleCard.svelte';
	import beansproutLogoSmall from '$lib/assets/beansprout-logo-small-white.svg';
	import Spinner from '$lib/Spinner.svelte';
	import { getImageUrl, getSignedColor } from '$lib/utils';
	import { toReadable } from '$lib/utils/svelte-utils.svelte';
	import { fetchQuotes } from '$lib/utils/symbols';
	import { createQuery } from '@tanstack/svelte-query';
	import { ArrowDown, ArrowUp, ChevronDown } from 'lucide-svelte';
	import qs from 'qs';

	interface Props {
		ticker: string;
		showPastReports?: boolean;
		beansproutReportsCount?: number;
	}

	let { ticker, showPastReports = true, beansproutReportsCount = $bindable(-1) }: Props = $props();

	let visibleCount = $state(3);

	async function getBeansproutReports(symbol: string): Promise<{
		currency;
		beansproutReports: Populated<BaseArticle, 'beansproutRating' | 'cover' | 'themes'>[];
		latestReport: Populated<BaseArticle, 'beansproutRating' | 'cover' | 'themes'>;
		targetChange: number;
		potentialUpsideDownside: number;
	}> {
		const { regularMarketPrice: price, currency } = await fetchQuotes([symbol]).then(
			(res) => res[symbol]
		);
		const beansproutReports = await fetch(
			`/api/beansprout-reports?${qs.stringify({ symbol })}`
		).then((res) => {
			if (!res.ok) throw new Error('Failed to fetch Beansprout reports');
			return res.json() as unknown as Populated<
				BaseArticle,
				'beansproutRating' | 'cover' | 'themes'
			>[];
		});
		beansproutReportsCount = beansproutReports.length;
		if (beansproutReportsCount === 0)
			throw new Error('No Beansprout reports found for this symbol');
		const latestReport = beansproutReports[0];
		const targetChange =
			latestReport.beansproutRating.target -
			(beansproutReports[1]?.beansproutRating?.target ?? latestReport.beansproutRating.target);
		const potentialUpsideDownside = (latestReport.beansproutRating.target - price) / price;
		return {
			currency,
			beansproutReports,
			latestReport,
			targetChange,
			potentialUpsideDownside
		};
	}

	const beansproutReportsQuery = createQuery(
		toReadable(() => ({
			queryKey: ['beansproutReports', ticker],
			queryFn: () => getBeansproutReports(ticker)
		}))
	);
</script>

{#if $beansproutReportsQuery.isLoading}
	<Spinner />
{:else if $beansproutReportsQuery.isSuccess}
	{@const { currency, beansproutReports, latestReport, targetChange, potentialUpsideDownside } =
		$beansproutReportsQuery.data}
	{#if beansproutReports.length}
		<div class="bg-green-dark rounded-2xl">
			<header class="flex items-center p-3">
				<img src={beansproutLogoSmall} alt="beansprout logo small" class="h-5 w-5" />
				<h3 class="body-bold text-white-pure ml-2">Beansprout Exclusive</h3>
			</header>
			<div class="border-gray-faint bg-white-pure rounded-2xl border-1 p-4">
				<dl
					class="mb-5 grid grid-cols-2 grid-rows-[auto_auto] gap-y-2 lg:grid-flow-col lg:grid-cols-3"
				>
					<dt class="body-bold">Rating:</dt>
					<dd>
						<p
							style="color: var(--color-{getSignedColor(
								latestReport.beansproutRating.rating === 'Buy'
									? 1
									: latestReport.beansproutRating.rating === 'Neutral'
										? 0
										: -1
							)})"
							class="body-bold mb-1"
						>
							{latestReport.beansproutRating.rating}
						</p>
						{#if beansproutReports.length > 1}
							<p class="card-regular text-gray-mid">
								(prev {beansproutReports[1].beansproutRating.rating})
							</p>
						{/if}
					</dd>
					<dt class="body-bold">Price Target*:</dt>
					<dd>
						<div
							class="body-bold mb-1 flex items-center gap-2"
							style="color: var(--color-{getSignedColor(targetChange)})"
						>
							{latestReport.beansproutRating.target.toLocaleString('default', {
								style: 'currency',
								currency,
								currencyDisplay: 'narrowSymbol'
							})}
							{#if targetChange > 0}
								<ArrowUp class="text-green-ticker h-4 w-4" />
							{:else if targetChange < 0}
								<ArrowDown class="text-red-ticker h-4 w-4" />
							{/if}
						</div>
						{#if beansproutReports.length > 1}
							<p class="card-regular text-gray-mid">
								(prev {beansproutReports[1].beansproutRating.target.toLocaleString('default', {
									style: 'currency',
									currency,
									currencyDisplay: 'narrowSymbol'
								})})
							</p>
						{/if}
					</dd>
					<dt class="body-bold">Upside/Downside:</dt>
					<dd
						class="body-bold"
						style="color: var(--color-{getSignedColor(potentialUpsideDownside)})"
					>
						{potentialUpsideDownside.toLocaleString('default', {
							style: 'percent',
							minimumFractionDigits: 2,
							maximumFractionDigits: 2,
							signDisplay: 'exceptZero'
						})}
					</dd>
				</dl>
				<p class="small-regular text-gray-dark">*Target price is for 12 months</p>

				{#if beansproutReports.length && showPastReports}
					<section class="mt-8">
						<h2 class="h3 mb-3">Research Reports</h2>
						<div class="divide-gray-light -mt-4 grid divide-y">
							{#each beansproutReports.slice(0, visibleCount) as article (article.id)}
								<ArticleCard
									title={article.title}
									thumbnail={getImageUrl(article.cover)}
									alt={article.cover.alternativeText}
									slug={article.slug}
									publishedAt={article.publishedAt}
									themes={article.themes.map((theme) => theme.name)}
								/>
							{/each}
						</div>
						{#if beansproutReports.length > visibleCount}
							<div class="mt-5 text-center lg:text-left">
								<button onclick={() => (visibleCount += 3)} class="link card-bold inline-flex"
									>VIEW MORE <ChevronDown class="w-6" /></button
								>
							</div>
						{/if}
					</section>
				{/if}
			</div>
		</div>
	{/if}
{/if}
